import { Injectable } from '@angular/core';
import { CoreProvider, fetchTimeout } from '../core.provider';
import { GameDto, EventDto, GameModeType } from '../local-data/games.service';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { mapCloudGameToLocalGame, mapCloudPbpToLocalPbp, mapLocalGameToCloudGame, mapLocalPbpToCloudPbp } from '../local-data/games.service.helper';
import { Mutex } from '../auth/mutex';
import { TeamModelDto } from '../api/models/team-model-dto';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

export class LogicError extends Error {
  static readonly type = 'LogicError';
  constructor(message: string) {
    super(message);
  }
}

export type GameTrackerDto = {
  game: GameDto;

  modified: number;
  events: EventDto[];
}

export type MinimalStats = {
  score: string;
  homeTeamGoals: number;
  visitorTeamGoals: number;
}
@Injectable({
  providedIn: 'root'
})
export class GameTrackerService {

  public readonly debugAutoGameStart: boolean = false;

  connectionMode: 'camera' | 'bench' | 'bench-server' = 'bench';
  localConnectionStatus$ = new BehaviorSubject<'connected'|'unstable'|'disconnected'>('disconnected');
  cloudConnectionStatus$ = new BehaviorSubject<'connected'|'unstable'|'disconnected'>('disconnected');
  cameraDeviceUrl?: string;
  cloudUserUuid?: string;
  firstGameInfoRequest$ = new Subject<boolean>();
  gameEvents$ = new BehaviorSubject<EventDto[]>([]);
  minimalStats$ = new BehaviorSubject<MinimalStats>({
    score: '0-0',
    homeTeamGoals: 0,
    visitorTeamGoals: 0,
  });

  gameTimer$ = new BehaviorSubject({
    counterSecSinceStart: 0,
    halfTime: 'T1',
    minutesSinceHalftime: 0,
    secondsSinceHalftime: 0,
    gameCounter: '00:00',
    worldClock: Date.now(),
  });

  benchDevicePollingInterval?: any;

  public get cameraModeEnabled() {
    return this.core?.platform.is('android') || this.core?.isIOS || (environment.enableWebGameTracking && this.core?.platform.is('desktop'));
  }

  /**
   * Whether this device can record from an external HLS camera.
   *
   * Narrower than {@link cameraModeEnabled}: reading an HLS stream needs Media Source Extensions, and
   * the iOS WKWebView only exposes them (as `ManagedMediaSource`) from 17.1. On anything older the
   * option is hidden and the device keeps recording with its own camera, which still works.
   */
  public get externalCameraEnabled() {
    return !!this.cameraModeEnabled && !!this.core?.hlsCaptureService.isSupported;
  }

  /**
   * HLS playlist the camera device is currently recording from, or undefined when it is using its own
   * camera. Mirrors `gameDto.game.externalCameraUrl`, which is the persisted copy.
   */
  externalCameraUrl?: string;

  public gameDtoStore?: GameTrackerDto;
  public get gameDto() {
    return this.gameDtoStore;
  }
  get isTraining(): boolean {
    const game = this.gameDtoStore?.game;
    return !!game && game.homeTeam?.localId === game.visitorTeam?.localId;
  }

  private core?: CoreProvider;

  constructor() {
    this.resetSubjects();
  }

  resetSubjects() {
    if (this.benchDevicePollingInterval) {
      clearInterval(this.benchDevicePollingInterval);
      this.benchDevicePollingInterval = undefined;
    }
    if (this.cloudGamePushingInterval) {
      clearInterval(this.cloudGamePushingInterval);
      this.cloudGamePushingInterval = undefined;
    }
    if (this.cloudVideoUploadingInterval) {
      clearInterval(this.cloudVideoUploadingInterval);
      this.cloudVideoUploadingInterval = undefined;
    }
    // Optional chaining: the constructor calls resetSubjects() before initCore() sets `core`.
    // Dropping the cache here is what makes a later post-game visit resolve to cloud video.
    this.core?.ldVideoCacheService.stop();
    // Releases the hls.js instance and its buffers; without this a finished match keeps pulling the
    // external stream over the arena uplink for as long as the app is open.
    this.core?.hlsCaptureService.detach();
    this.localLastCorrectPolling = 0;
    this.cloudLastCorrectPolling = 0;
    this.cloudLastUpdatedAt = '';
    this.pendingCloudDeleteUuids = [];
    this.pushingCloudLastUpdatedAt = '';
    this.syncFromCloudIteration = 0;
    this.connectionMode = 'bench';
    this.cameraDeviceUrl = undefined;
    this.externalCameraUrl = undefined;
    this.gameDtoStore = undefined;
    this.firstGameInfoRequest$ = new Subject<boolean>();
    this.gameEvents$.next([]);
    this.localConnectionStatus$.next('disconnected');
    this.cloudConnectionStatus$.next('disconnected');
    this.minimalStats$.next({
      score: '0-0',
      homeTeamGoals: 0,
      visitorTeamGoals: 0,
    });
    this.stopGameTimer();
    this.gameTimer$.next({
      counterSecSinceStart: 0,
      halfTime: 'T1',
      minutesSinceHalftime: 0,
      secondsSinceHalftime: 0,
      gameCounter: '00:00',
      worldClock: Date.now(),
    });
  }

  async initCore(core: CoreProvider) {
    this.core = core;
  }

  //#region Game load/unload methods
  parseConnectionString(connectionString: string): { user: string, localHostPort: string, gameLocalId: string, gameAccessHash: string } {
    const connectStringParts = connectionString.replace('haibench://', '').split('/');
    const user = connectStringParts[0].split('@')[0];
    const localHostPort = connectStringParts[0].split('@')[1];
    const gameLocalId = connectStringParts[1];
    const gameAccessHash = connectStringParts[2];
    console.log('parseConnectionString', connectionString, { user, localHostPort, gameLocalId, gameAccessHash });
    return { user, localHostPort, gameLocalId, gameAccessHash };
  }

  async loadLiveBenchFromConnectionString(connectionString: string): Promise<boolean> {
    const loading = await this.core!.genericLoading(this.core!.trans.instant(_('Checking connection to camera device...')));
    try {
      const { user, localHostPort, gameLocalId, gameAccessHash } = this.parseConnectionString(connectionString);
      const insideSameAccount = user === (this.core!.auth.user?.sub ?? 'no-user');

      this.resetSubjects();

      this.connectionMode = 'bench';
      const connectionTypesAvailable: ('local' | 'cloud')[] = [];

      //#region Attempt local connection
      this.cameraDeviceUrl = localHostPort ? `https://${localHostPort}` : undefined;
      this.cloudUserUuid = user;

      try {
        if (!this.cameraDeviceUrl) throw new Error('Camera device url not set');
        const gameInfo = await fetchTimeout(`${this.cameraDeviceUrl}/game-info/${gameLocalId}`);
        if (!gameInfo.ok) throw new Error('Request error: ' + gameInfo.status);
        const gameInfoResponse = await gameInfo.json() as {result: boolean, game?: GameTrackerDto};
        if (!gameInfoResponse.result) throw new Error('Game not found');
        // this.loadGameFromRemote(gameInfoResponse.game!, insideSameAccount);

        this.startNewGame(gameInfoResponse.game!.game, gameInfoResponse.game!.game.gameMode, insideSameAccount);
        this.gameDtoStore!.events = gameInfoResponse.game!.events;
        this.gameDtoStore!.modified = gameInfoResponse.game!.modified;
        this.fireGameEvents();

        this.localConnectionStatus$.next('connected');
        connectionTypesAvailable.push('local');
      } catch (error) {
        console.error('Error connecting to camera device via local connection', error);
      }
      //#endregion

      //#region Attempt cloud connection
      try {
        const resp = await firstValueFrom(this.core!.api.gameTrackerPublic.gameTrackerPublicControllerGetGameInfo$Response({
          userUuid: this.cloudUserUuid,
          accessHash: gameAccessHash,
          localId: gameLocalId,
        }));
        if (!resp.ok) throw new Error('Request error: ' + resp.status);

        if (connectionTypesAvailable.length === 0) {
          const teamsMapByServerId = {} as Record<string, TeamModelDto>;
          teamsMapByServerId[resp.body.homeTeam.uuid!] = { uuid: resp.body.homeTeam.uuid!, name: resp.body.homeTeam.name, localId: resp.body.homeTeam.localId };
          teamsMapByServerId[resp.body.visitorTeam.uuid!] = { uuid: resp.body.visitorTeam.uuid!, name: resp.body.visitorTeam.name, localId: resp.body.visitorTeam.localId };

          // If no connection types available, it means that the game is not initialised so initialise from cloud
          const gameDto = mapCloudGameToLocalGame(resp.body.game, teamsMapByServerId, undefined);
          this.startNewGame(gameDto, gameDto.gameMode, insideSameAccount);
        }

        this.cloudConnectionStatus$.next('connected');
        connectionTypesAvailable.push('cloud');
      } catch (error) {
        console.error('Error connecting to camera device via cloud connection', error);
      }
      //#endregion

      console.log('Connection types available', connectionTypesAvailable);

      if (connectionTypesAvailable.length === 0) throw new Error('No connection types available');
      this.goToGameTracker();
      return true;
    } catch (error) {
      console.error('Error connecting to camera device', error);
      this.core!.errorToast(loading, this.core!.trans.instant(_('Error connecting to camera device, ensure you are on the same network as the camera device or that both devices are connected to internet')));
      return false;
    } finally {
      loading.dismiss();
    }
  }

  async loadGameFromCloud(gameDto: GameDto) {
    if (gameDto.uuid) {
      this.resetSubjects();
      this.connectionMode = 'bench';
      this.cloudUserUuid = this.core!.auth.user?.sub;
      const cloudEvents = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerGetGamePbp({ uuid: gameDto.uuid }));
      const events = cloudEvents.sort((a, b) => a.orderId - b.orderId).map(e => mapCloudPbpToLocalPbp(e, gameDto));
      this.gameDtoStore = { game: gameDto, modified: 0, events };
      // await this.core!.native.setupGameTrackerServer();
      this.fireGameEvents();
      console.log('Loaded game from cloud', this.gameDtoStore);
      return true;
    } else {
      return false;
    }
  }

  async loadGameFromStorage(gameDto: GameDto): Promise<boolean> {
    if (gameDto.trackerMode === 'camera' && this.cameraModeEnabled) {
      this.resetSubjects();
      this.cloudUserUuid = this.core!.auth.user?.sub;
      this.connectionMode = gameDto.gameStatus === 'live' ? 'camera' : 'bench-server'; // if put 'camera' mode here, it will be like continue tracking feature
      const events = await this.core?.ldGamesService.getGameEvents(gameDto.localId);
      if (!events) { this.core?.errorToast(undefined, this.core!.trans.instant(_('Failed to load game events'))); return false; }
      this.gameDtoStore = { game: gameDto, modified: 0, events };
      if (this.core?.isAndroid || this.core?.isIOS) await this.core!.native.setupGameTrackerServer();
      if (this.connectionMode === 'camera') {
        // Resuming a match that was being filmed by an external camera has to go back to that camera,
        // not silently fall back to the device's own — the phone is probably not even pointing at the court.
        this.externalCameraUrl = this.externalCameraEnabled ? gameDto.externalCameraUrl : undefined;
        this.startCloudGamePushing();
        const lastEvent = events[events.length - 1];
        this.gameTimer$.next({
          counterSecSinceStart: lastEvent?.gameSecondsSinceStart ?? 0,
          halfTime: lastEvent?.halftime ?? 'T1',
          minutesSinceHalftime: lastEvent?.minutesSinceHalftime ?? 0,
          secondsSinceHalftime: lastEvent?.secondsSinceHalftime ?? 0,
          gameCounter: `${(lastEvent?.minutesSinceHalftime ?? 0).toString().padStart(2, '0')}:${(lastEvent?.secondsSinceHalftime ?? 0).toString().padStart(2, '0')}`,
          worldClock: Date.now(),
        });
      }
      this.fireGameEvents();
      console.log('Loaded game from storage', this.gameDtoStore);
      return true;
    } else {
      return false;
    }
  }

  /**
   * Switch the camera device between its own camera and an external HLS stream mid-match.
   *
   * Persisted on the game so the choice survives an app restart. `preventCloudUpdate` is deliberate:
   * this is a local capture setting the backend has no column for, and pushing the whole game just to
   * record it would be a pointless round trip on a link that is already the bottleneck.
   */
  async setExternalCameraUrl(url?: string): Promise<void> {
    this.externalCameraUrl = url;
    if (!this.gameDtoStore) return;
    this.gameDtoStore.game.externalCameraUrl = url;
    try {
      await this.core!.ldGamesService.updateGame(this.gameDtoStore.game);
    } catch (error) {
      // The stream still works for this session; only the "remember it" part failed.
      console.error('Could not persist the external camera URL', error);
    }
  }

  /**
   * Start a new game (from bench or camera but only applicable for live flows, accessing from cloud or storage as bench sever does not applies).
   * Currently accessing as camera mode from continue tracking is neither using this method.
   *
   * @param gameDto - Game data
   * @param gameMode - Game mode
   */
  startNewGame(gameDto: Omit<GameDto, 'localId'|'trackerMode'|'gameMode'|'gameStatus'|'createdAt'|'localLastUpdate'|'pendingDelete'|'accessHash'> & {accessHash?: string}, gameMode: GameModeType = 'rookie', insideSameAccount = true) {
    if (this.connectionMode === 'bench-server') return;
    this.gameDtoStore = {
      game: {
        localId: crypto.randomUUID(),
        ...gameDto,
        gameMode,
        gameStatus: 'live',
        accessHash: gameDto.accessHash ?? crypto.randomUUID(),
        trackerMode: this.connectionMode,
        createdAt: new Date().toISOString(),
        localLastUpdate: null,
        pendingDelete: false,
      },
      modified: 0,
      events: [],
    };
    this.fireGameEvents();

    if (!this.cloudUserUuid) {
      this.cloudUserUuid = this.core!.auth.user?.sub;
    }
    if (insideSameAccount) {
      this.core!.ldGamesService.addGame(this.gameDtoStore.game);
    }

    if (this.connectionMode === 'camera') {
      this.externalCameraUrl = this.externalCameraEnabled ? this.gameDtoStore.game.externalCameraUrl : undefined;
      this.startCloudGamePushing();
    }
    if (this.connectionMode === 'bench') {
      // this.localConnectionStatus$.next('connected'); // Assume connected when opening bench having camera device url
      this.startPollingGameEvents();
      // Live bench sessions keep a local copy of every clip so reviewing does not depend on the network.
      // Only reached from the live path — post-game review goes through loadGameFromCloud instead.
      this.core!.ldVideoCacheService.start(this.gameDtoStore.game);
    }
  }

  async endGame() {
    this.stopGameTimer();
    this.gameDtoStore!.game.gameStatus = 'ended';
    this.gameDtoStore!.game.accessHash = crypto.randomUUID();
    if (this.gameDtoStore!.game.uuid) {
      // This will ensure that a full game sync will be done after game ends
      this.gameDtoStore!.game.localLastUpdate = Date.now();
    }
    await this.core!.ldGamesService.updateGame(this.gameDtoStore!.game);
    this.resetSubjects();
  }

  async goToStartGame() {
    const userLoggedIn = true; // this.core!.platform.is('android');
    if (!userLoggedIn) {
      await this.core!.router.navigateByUrl('/login');
    } else {
      // Always go to start-game page - the component will handle device compatibility
      await this.core!.router.navigateByUrl('/game-tracker/start-game');
    }
  }

  goToGameTracker() {
    if (this.connectionMode === 'camera') {
      this.core!.navCtrl.navigateRoot('/game-tracker/track');
    } else {
      this.core!.navCtrl.navigateRoot('/game-tracker/view-events');
    }
  }
  //#endregion

  //#region Camera device methods

  public get isTimerRunning() {
    return this.gameTimerInterval !== undefined;
  }
  private gameTimerInterval?: any;
  startGameTimer() {
    if (this.gameTimerInterval) return;
    this.gameTimerInterval = setInterval(() => {
      // Calculate next timer tick values
      const minSecSinceHalftime = this.gameTimer$.getValue().minutesSinceHalftime * 60 + this.gameTimer$.getValue().secondsSinceHalftime + 1;
      const minutesSinceHalftime = Math.floor(minSecSinceHalftime / 60);
      const secondsSinceHalftime = minSecSinceHalftime % 60;
      this.gameTimer$.next({
        counterSecSinceStart: this.gameTimer$.getValue().counterSecSinceStart + 1,
        halfTime: this.gameTimer$.getValue().halfTime,
        minutesSinceHalftime: minutesSinceHalftime,
        secondsSinceHalftime: secondsSinceHalftime,
        gameCounter: `${minutesSinceHalftime.toString().padStart(2, '0')}:${secondsSinceHalftime.toString().padStart(2, '0')}`,
        worldClock: Date.now(),
      });
    }, 1000);
  }
  stopGameTimer() {
    if (this.gameTimerInterval) {
      clearInterval(this.gameTimerInterval);
    }
    this.gameTimerInterval = undefined;
  }

  async addEvent(eventInfo: Omit<EventDto, 'gameLocalId' | 'orderId' | 'localId' | 'eventDate' | 'cloudVideoUploaded'>) {
    if (this.connectionMode !== 'camera') return;
    const event: EventDto = {
      ...eventInfo,
      gameLocalId: this.gameDtoStore!.game.localId,
      orderId: (this.gameEvents$.getValue()[0]?.orderId ?? 0) + 1,
      localId: crypto.randomUUID(),
      cloudVideoUploaded: false,
      eventDate: new Date(this.gameTimer$.getValue().worldClock),
      gameSecondsSinceStart: this.gameTimer$.getValue().counterSecSinceStart,
      halftime: this.gameTimer$.getValue().halfTime,
      minutesSinceHalftime: this.gameTimer$.getValue().minutesSinceHalftime,
      secondsSinceHalftime: this.gameTimer$.getValue().secondsSinceHalftime,
    };

    this.gameDtoStore!.events.push(event);
    await this.core!.ldGamesService.addGameEvent({...event, videoBlob: undefined});
    console.log('Added event', event);
    this.fireGameEvents();
  }

  async deleteEvent(eventLocalId: string, preventCloudUpdate: boolean = false) {
    if (this.connectionMode === 'camera' || this.connectionMode === 'bench-server') {
      setTimeout(async () => {
        // Continue async to avoid concurrent cordova of different plugins calls (giving native handler 100ms to process)
        const event = this.gameDtoStore!.events.find(e => e.localId === eventLocalId);
        const updatedEvents = await this.core!.ldGamesService.deleteGameEvent(this.gameDtoStore!.game.localId, eventLocalId);
        this.gameDtoStore!.events = updatedEvents;
        this.gameDtoStore!.modified = Date.now();
        this.fireGameEvents();

        if (event?.uuid && !preventCloudUpdate) {
          this.pendingCloudDeleteUuids.push(event.uuid);
        }
      }, 100);

    } else if (this.connectionMode === 'bench') {
      this.fireGameEvents(); // Fire change and notify master camera device

      let deleted = false;
      try {
        if (!this.cameraDeviceUrl) throw new Error('Camera device url not found');
        const response = await fetchTimeout(`${this.cameraDeviceUrl}/delete-event/` + this.gameDtoStore!.game.localId+ '/'+eventLocalId, { method: 'DELETE' });
        if (!response.ok || (await response.json()).result === false) throw new Error('Failed to delete event at camera device');
        deleted = true;
      } catch (error) {
        console.error('Error deleting event at camera device', error);
      }

      if (!deleted) {
        // Attempt to delete event from cloud
        try {
          const event = this.gameDtoStore!.events.find(e => e.localId === eventLocalId);
          if (!event) throw new Error('Event not found');
          if (!event.uuid) throw new Error('Event is not present at cloud');

          await firstValueFrom(this.core!.api.gameTrackerPublic.gameTrackerPublicControllerDeleteGamePbp({
            userUuid: this.cloudUserUuid!,
            accessHash: this.gameDtoStore!.game.accessHash!,
            localId: this.gameDtoStore!.game.localId,
            pbpUuid: event.uuid,
          }));
          deleted = true;
        } catch (error) {
          console.error('Error deleting event at cloud', error);
        }
      }

      if (!deleted) {
        this.core!.errorToast(undefined, this.core!.trans.instant(_('Failed to delete event at camera device and cloud')));
      } else if (this.gameDtoStore!.game.gameStatus === 'ended') {
        this.gameDtoStore!.events = this.gameDtoStore!.events.filter(e => e.localId !== eventLocalId);
        this.fireGameEvents();
      }

    }
  }

  async updateEvent(event: EventDto, preventCloudUpdate: boolean = false) {
    const actualEvent = this.gameDtoStore!.events.find(e => e.localId === event.localId);
    if (!actualEvent) throw new Error('Event not found');

    const originalEvent = { ...actualEvent };

    if (this.connectionMode === 'camera') {
      actualEvent.uuid = event.uuid;
      actualEvent.cloudVideoUploaded = event.cloudVideoUploaded;
    }
    actualEvent.important = event.important;
    actualEvent.lastPresignedCloudVideoTimestamp = event.lastPresignedCloudVideoTimestamp;
    actualEvent.lastPresignedCloudVideoUrl = event.lastPresignedCloudVideoUrl;

    if (this.connectionMode === 'camera' || this.connectionMode === 'bench-server') {
      setTimeout(async () => {
        // Continue async to avoid concurrent cordova of different plugins calls (giving native handler 100ms to process)
        if (!preventCloudUpdate) actualEvent.pendingUpdate = true;
        await this.core!.ldGamesService.updateGameEvent({...actualEvent, videoBlob: undefined});
        this.gameDtoStore!.modified = Date.now();
        this.fireGameEvents();

      }, 100);
    } else if (this.connectionMode === 'bench') {
      this.fireGameEvents(); // Fire change and notify master camera device
      let updated = false;
      try {
        if (!this.cameraDeviceUrl) throw new LogicError('Camera device url not found');
        const response = await fetchTimeout(`${this.cameraDeviceUrl}/update-event/` + this.gameDtoStore!.game.localId, { method: 'POST', body: JSON.stringify(actualEvent) });
        if (!response.ok || (await response.json()).result === false) throw new LogicError('Failed to update event at camera device');
        updated = true;
      } catch (error) {
        // Silence logic errors (still cloud update will be attempted)
        if (!(error instanceof LogicError)) {
          console.error('Error updating event at camera device', error);
        } else {
          console.debug('Cannot update event at camera device, continue with cloud update', error);
        }
      }

      if (!updated) {
        // Attempt to update event at cloud
        try {
          await firstValueFrom(this.core!.api.gameTrackerPublic.gameTrackerPublicControllerDeleteGameUpdateImportant({
            userUuid: this.cloudUserUuid!,
            accessHash: this.gameDtoStore!.game.accessHash!,
            localId: this.gameDtoStore!.game.localId,
            pbpUuid: actualEvent.uuid!,
            important: actualEvent.important,
          }));
          updated = true;
        } catch (error) {
          console.error('Error updating event at cloud', error);
        }
      }

      if (!updated) {
        this.core!.errorToast(undefined, this.core!.trans.instant(_('Failed to update event at camera device and cloud')));
        actualEvent.important = originalEvent.important;
        actualEvent.lastPresignedCloudVideoTimestamp = originalEvent.lastPresignedCloudVideoTimestamp;
        actualEvent.lastPresignedCloudVideoUrl = originalEvent.lastPresignedCloudVideoUrl;
        this.fireGameEvents();
      }

    } else if (this.connectionMode === 'bench' && !this.cameraDeviceUrl) {
      // Bench oppened from cloud (usually post live)
    }
  }

  fireGameEvents() {
    const tickEvents = this.gameDtoStore!.events.sort((a, b) => b.orderId - a.orderId)
    this.gameEvents$.next(tickEvents);
    this.minimalStats$.next(this.calculateMinimalStats(tickEvents));
  }

  private calculateMinimalStats(tickEvents: EventDto[]) {
    const minimalStats: MinimalStats = {
      score: `0-0`,
      homeTeamGoals: 0,
      visitorTeamGoals: 0,
    };
    for (const event of tickEvents) {
      if (event.teamMarker === 'home' && event.eventType === 'GOAL') {
        minimalStats.homeTeamGoals++;
      } else if (event.teamMarker === 'visitor' && event.eventType === 'GOAL') {
        minimalStats.visitorTeamGoals++;
      }
    }
    minimalStats.score = `${minimalStats.homeTeamGoals}-${minimalStats.visitorTeamGoals}`;
    return minimalStats;
  }

  private cloudGamePushingInterval?: any;
  private cloudVideoUploadingInterval?: any;
  private pendingCloudDeleteUuids: string[] = [];
  private pushingCloudLastUpdatedAt: string = '';
  private syncFromCloudIteration: number = 0;
  startCloudGamePushing() {
    if (this.connectionMode !== 'camera') return;
    if (this.cloudGamePushingInterval) clearInterval(this.cloudGamePushingInterval);
    this.cloudGamePushingInterval = setInterval(async () => {
      // Just in case the mode changed (disable polling)
      if (this.connectionMode !== 'camera' || !this.gameDtoStore) {
        clearInterval(this.cloudGamePushingInterval!);
        this.cloudGamePushingInterval = undefined;
        return;
      }
      // Skip cloud sync attempt if not connected to cloud
      if (!this.core?.api.connectedToCloud$.value) return;

      const {unlock, error} = await Mutex.getLock('game-tracker-cloud-game-pushing').lock(true, true);
      if ((error as Error & { customCode?: string })?.customCode === 'ALREADY_WAITING') return;
      try {
        if (!this.gameDtoStore!.game.uuid) {
          const teamsMapByLocalId = (await this.core!.ldTeamsService.getTeams()).reduce((acc, team) => {
            acc[team.localId] = team;
            return acc;
          }, {} as Record<string, TeamModelDto>);

          let localConnectionInfo = 'no-info';
          if (this.core?.isAndroid || this.core?.isIOS || (this.core?.isWeb && environment.enableWebGameTracking)) localConnectionInfo = await this.core!.native.setupGameTrackerServer();

          const cloudGame = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerCreateGame({
            body: await mapLocalGameToCloudGame(this.gameDtoStore!.game, teamsMapByLocalId, undefined, localConnectionInfo)
          }));
          this.gameDtoStore!.game.uuid = cloudGame.uuid;
          this.core!.ldGamesService.updateGame(this.gameDtoStore!.game);
        }

        if (this.gameDtoStore!.game.uuid) {
          // Upload all missing cloud events
          while (this.gameDtoStore!.events.some(e => !e.uuid)) {
            const event = this.gameDtoStore!.events.find(e => !e.uuid)!;
            const cloudEvent = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerCreateGamePbp({ uuid: this.gameDtoStore!.game.uuid, body: mapLocalPbpToCloudPbp(event, this.gameDtoStore!.game)}));
            event.uuid = cloudEvent.uuid;
            event.pendingUpdate = false;
            await this.updateEvent(event, true);
          }

          while (this.gameDtoStore!.events.some(e => e.uuid && e.pendingUpdate)) {
            const event = this.gameDtoStore!.events.find(e => e.uuid && e.pendingUpdate)!;
            const cloudEvent = await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerUpdateGamePbp({ uuid: this.gameDtoStore!.game.uuid, pbpUuid: event.uuid!, body: mapLocalPbpToCloudPbp(event, this.gameDtoStore!.game)}));
            event.uuid = cloudEvent.uuid;
            event.pendingUpdate = false;
            await this.updateEvent(event, true);
          }

          // Sync from cloud every 3 iterations (to avoid too many requests) ~ 3 seconds
          this.syncFromCloudIteration = ((this.syncFromCloudIteration ?? 0) + 1) % 3;
          if (this.syncFromCloudIteration === 0) {
            try {
              const resp = await firstValueFrom(this.core!.api.gameTrackerPublic.gameTrackerPublicControllerGetGamePbp({
                userUuid: this.cloudUserUuid!,
                accessHash: this.gameDtoStore!.game.accessHash!,
                localId: this.gameDtoStore!.game.localId,
                updatedAfter: this.pushingCloudLastUpdatedAt || undefined,
              }));
              this.pushingCloudLastUpdatedAt = resp.lastUpdatedAt || '';

              for (const event of resp.pbp) {
                // If event is not in local events, add it
                if (!this.gameDtoStore!.events.some(e => e.localId === event.localId)) {
                  console.warn('Skip to create cloud event at camera device', event);
                } else {
                  // If event is in local events, update it
                  const localEvent = this.gameDtoStore!.events.find(e => e.localId === event.localId);
                  if (localEvent) {
                    // Update only fields that can be updated from cloud
                    localEvent.important = event.important;
                    this.updateEvent(localEvent, true);
                  }
                }
              }

              for (const ce of resp.deletedPbps) {
                if (this.gameDtoStore!.events.some(e => e.localId === ce.localId)) {
                  this.deleteEvent(ce.localId, true);
                }
              }

              // Remove cloud deleted events
              this.gameDtoStore!.events = this.gameDtoStore!.events.filter(e => !resp.deletedPbps.some(d => d.uuid === e.uuid || d.localId === e.localId));

              this.fireGameEvents();

              // Check if localConnectionInfo has changed and update to cloud
              let newLocalConnectionInfo = 'no-info';
              if (this.core?.isAndroid || this.core?.isIOS || (this.core?.isWeb && environment.enableWebGameTracking)) newLocalConnectionInfo = await this.core!.native.setupGameTrackerServer();
              if (resp.localConnectionInfo !== newLocalConnectionInfo) {
                await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerUpdateGameLocalConnectionInfo({ uuid: this.gameDtoStore!.game.uuid, localConnectionInfo: newLocalConnectionInfo }));
              }

            } catch (error) {
              console.error('Error syncing from cloud', error);
            }
          }

          if (this.pendingCloudDeleteUuids.length > 0) {
            for (const uuid of this.pendingCloudDeleteUuids) {
              try {
                await firstValueFrom(this.core!.api.gameTracker.gameTrackerControllerDeleteGamePbp({ uuid: this.gameDtoStore!.game.uuid, pbpUuid: uuid }));
                this.pendingCloudDeleteUuids = this.pendingCloudDeleteUuids.filter(u => u !== uuid);
              } catch (error) {
                console.error('Error deleting event at cloud', error);
              }
            }
          }
        }

      } catch (error) {
        console.error('Error pushing game to cloud', error);
      } finally {
        unlock(null);
      }
    }, 1000);

    if (!this.cloudVideoUploadingInterval) clearInterval(this.cloudVideoUploadingInterval);
    this.cloudVideoUploadingInterval = setInterval(async () => {
      if (this.connectionMode !== 'camera') return;
      if (!this.core?.api.connectedToCloud$.value) return;

      const {unlock, error} = await Mutex.getLock('game-tracker-cloud-video-uploading').lock(true, true);
      if ((error as Error & { customCode?: string })?.customCode === 'ALREADY_WAITING') return;
      try {
        if (!this.gameDtoStore!.game.uuid) return;

        while (this.gameDtoStore!.events.some(event => event.uuid && !event.cloudVideoUploaded)) {
          // Take the last event that is not uploaded yet
          let event = this.gameDtoStore!.events.filter(event => event.uuid && !event.cloudVideoUploaded && !event.failedVideoUploaded).sort((a, b) => b.orderId - a.orderId)[0];
          if (!event) event = this.gameDtoStore!.events.filter(event => event.uuid && !event.cloudVideoUploaded).sort((a, b) => b.orderId - a.orderId)[0];
          await this.core!.ldGamesService.uploadEventVideo(event, this.gameDtoStore!.game);
          await this.updateEvent(event);
        }
      } catch (error) {
        console.error('Error uploading video to cloud', error);
      } finally {
        unlock(null);
      }
    }, 1000);
  }
  //#endregion

  //#region Bench device methods
  private localLastCorrectPolling: number = 0;
  private cloudLastCorrectPolling: number = 0;
  private cloudLastUpdatedAt: string = '';
  async startPollingGameEvents() {
    if (this.benchDevicePollingInterval) clearInterval(this.benchDevicePollingInterval);
    this.benchDevicePollingInterval = setInterval(async () => {
      // Just in case the mode changed (disable polling)
      if (this.connectionMode !== 'bench') {
        clearInterval(this.benchDevicePollingInterval!);
        this.benchDevicePollingInterval = undefined;
        return;
      }

      const {unlock, error} = await Mutex.getLock('game-tracker-polling').lock(true, true);
      if ((error as Error & { customCode?: string })?.customCode === 'ALREADY_WAITING') return;
      try {

        try {
          const response = await fetchTimeout(`${this.cameraDeviceUrl}/game-events/` + this.gameDtoStore!.game.localId + '/' + (this.gameEvents$.getValue()[0]?.orderId ?? '0'));
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          if (!data.result) throw new Error('Failed to fetch game events');
          if (this.localConnectionStatus$.getValue() !== 'connected') {
            this.localConnectionStatus$.next('connected');
          }
          this.localLastCorrectPolling = Date.now() / 1000;
          if (data.events?.length) {
            // An event first seen through the cloud arrives with an empty videoUri (see mapCloudPbpToLocalPbp),
            // and without it the clip can be served neither over the local network nor from the local cache.
            // Adopt the file name as soon as the camera device tells us about it.
            for (const event of data.events as EventDto[]) {
              const localEvent = this.gameDtoStore!.events.find(e => e.localId === event.localId);
              if (localEvent && !localEvent.videoUri && event.videoUri) localEvent.videoUri = event.videoUri;
            }
            this.gameDtoStore!.events.push(...data.events.filter((e: EventDto) => !this.gameDtoStore!.events.some(e2 => e2.localId === e.localId)));
          }
          this.fireGameEvents();

          // Modified flag (to fetch full model)
          if (data.modified && data.modified > this.gameDtoStore!.modified) {
            if (!this.cameraDeviceUrl) throw new Error('Camera device url not set');
            const gameInfo = await fetchTimeout(`${this.cameraDeviceUrl}/game-info/${this.gameDtoStore!.game.localId}`);
            if (!gameInfo.ok) throw new Error('Full request info after modified flag error');
            const gameInfoResponse = await gameInfo.json() as {result: boolean, game?: GameTrackerDto};
            if (!gameInfoResponse.result) throw new Error('Game not found');

            this.gameDtoStore!.events = gameInfoResponse.game!.events;
            this.gameDtoStore!.modified = data.modified;
            this.fireGameEvents();
          }
        } catch (error) {
          // console.error('Error on local connection', error);
          if (Date.now() / 1000 - this.localLastCorrectPolling > 20) {
            if (this.localConnectionStatus$.getValue() !== 'disconnected') {
              this.localConnectionStatus$.next('disconnected');
            }
          } else {
            if (this.localConnectionStatus$.getValue() !== 'unstable') {
              this.localConnectionStatus$.next('unstable');
            }
          }
        }

        try {
          const resp = await firstValueFrom(this.core!.api.gameTrackerPublic.gameTrackerPublicControllerGetGamePbp({
            userUuid: this.cloudUserUuid!,
            accessHash: this.gameDtoStore!.game.accessHash!,
            localId: this.gameDtoStore!.game.localId,
            updatedAfter: this.cloudLastUpdatedAt || undefined,
          }));
          if (this.cloudConnectionStatus$.getValue() !== 'connected') {
            this.cloudConnectionStatus$.next('connected');
          }
          this.cloudLastCorrectPolling = Date.now() / 1000;
          this.cloudLastUpdatedAt = resp.lastUpdatedAt || '';
          const localEventsFromCloud = resp.pbp.map(e => mapCloudPbpToLocalPbp(e, this.gameDtoStore!.game));
          for (const event of localEventsFromCloud) {
            // If event is not in local events, add it
            if (!this.gameDtoStore!.events.some(e => e.localId === event.localId)) {
              this.gameDtoStore!.events.push(event);
            } else {
              // If event is in local events, update it
              const localEvent = this.gameDtoStore!.events.find(e => e.localId === event.localId);
              if (localEvent) {
                localEvent.eventType = event.eventType;
                localEvent.important = event.important;
                localEvent.cloudVideoUploaded = event.cloudVideoUploaded;
                localEvent.lastPresignedCloudVideoTimestamp = event.lastPresignedCloudVideoTimestamp;
                localEvent.lastPresignedCloudVideoUrl = event.lastPresignedCloudVideoUrl;
                console.log('Updated event', localEvent);
              }
            }
          }

          // Remove cloud deleted events
          this.gameDtoStore!.events = this.gameDtoStore!.events.filter(e => !resp.deletedPbps.some(d => d.uuid === e.uuid || d.localId === e.localId));

          this.fireGameEvents();

          const localHostPort = this.parseConnectionString(resp.localConnectionInfo!).localHostPort;
          const newCamereDeviceUrl = localHostPort ? `https://${localHostPort}` : undefined;
          if (this.localConnectionStatus$.getValue() !== 'connected' && newCamereDeviceUrl !== this.cameraDeviceUrl) {
            this.cameraDeviceUrl = newCamereDeviceUrl;
          }

        } catch (error) {
          // console.error('Error on cloud connection', error);
          if (Date.now() / 1000 - this.cloudLastCorrectPolling > 20) {
            if (this.cloudConnectionStatus$.getValue() !== 'disconnected') {
              this.cloudConnectionStatus$.next('disconnected');
            }
          } else {
            if (this.cloudConnectionStatus$.getValue() !== 'unstable') {
              this.cloudConnectionStatus$.next('unstable');
            }
          }
        }

      } catch (error) {
        console.error('Error on polling', error);
      } finally {
        unlock(null);
      }
    }, 3000);
  }
  //#endregion


}
